import React, { useId } from "react";
import { colourHex } from "../../../utils/fashion/colourHex";
import { GARMENT_PATHS, garmentGround } from "../../../utils/fashion/garmentArt";

// Unknown colour names draw in a quiet slate rather than guessing a hue.
const FALLBACK_HEX = "#7C8591";

const FABRICS = {
  oxford: (id) => (
    <pattern id={id} width="3" height="3" patternUnits="userSpaceOnUse">
      <path d="M0 0h3M0 1.5h3" stroke="#fff" strokeOpacity=".22" strokeWidth=".5" />
      <path d="M0 0v3" stroke="#000" strokeOpacity=".08" strokeWidth=".5" />
    </pattern>
  ),
  denim: (id) => (
    <pattern id={id} width="3" height="3" patternUnits="userSpaceOnUse">
      <path d="M-1 1 1-1M0 3 3 0M2 4 4 2" stroke="#fff" strokeOpacity=".2" strokeWidth=".6" />
    </pattern>
  ),
  twill: (id) => (
    <pattern id={id} width="2.4" height="2.4" patternUnits="userSpaceOnUse">
      <path d="M0 2.4 2.4 0" stroke="#000" strokeOpacity=".12" strokeWidth=".5" />
    </pattern>
  ),
  linen: (id) => (
    <pattern id={id} width="8" height="4" patternUnits="userSpaceOnUse">
      <path d="M0 1h5M2 3h6" stroke="#fff" strokeOpacity=".28" strokeWidth=".45" />
      <path d="M6 1h2M0 3h1" stroke="#000" strokeOpacity=".08" strokeWidth=".45" />
    </pattern>
  ),
  pique: (id) => (
    <pattern id={id} width="2.2" height="2.2" patternUnits="userSpaceOnUse">
      <circle cx="1.1" cy="1.1" r=".45" fill="#fff" fillOpacity=".22" />
    </pattern>
  ),
  jersey: (id) => (
    <pattern id={id} width="1.6" height="4" patternUnits="userSpaceOnUse">
      <path d="M.8 0v4" stroke="#000" strokeOpacity=".07" strokeWidth=".5" />
    </pattern>
  ),
  crepe: (id) => (
    <pattern id={id} width="3" height="3" patternUnits="userSpaceOnUse">
      <circle cx=".7" cy=".9" r=".3" fill="#fff" fillOpacity=".18" />
      <circle cx="2.2" cy="2.3" r=".3" fill="#000" fillOpacity=".1" />
    </pattern>
  ),
};

// Relative luminance 0–1 of a #rgb/#rrggbb colour (null when it is not one).
function luminance(hex) {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return null;
  const six = m[1].length === 3 ? m[1].replace(/./g, "$&$&") : m[1];
  const n = parseInt(six, 16);
  const lin = (c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(n >> 16) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
}

// Black and navy drawn at full strength read as solid blocks on a tile, so dark
// colours are lifted a little toward white; the colour still reads as itself.
export function softFill(hex) {
  const lum = luminance(hex);
  if (lum === null || lum >= 0.08) return hex;
  const six = hex.length === 4 ? hex.slice(1).replace(/./g, "$&$&") : hex.slice(1);
  const n = parseInt(six, 16);
  const mix = (c) => Math.round(c + (255 - c) * 0.22);
  return `rgb(${mix(n >> 16)},${mix((n >> 8) & 255)},${mix(n & 255)})`;
}

// Seams and trims drawn over the body: light lines on dark cloth, dark on light.
function seamColour(hex) {
  const lum = luminance(hex);
  return lum !== null && lum < 0.25 ? "rgba(255,255,255,.38)" : "rgba(0,0,0,.22)";
}

// A shirt has a collar and a button placket; without them it reads as a tee.
const DETAILS = {
  shirt: (seam, fill) => (
    <>
      <path d="M32 17V58" fill="none" stroke={seam} strokeWidth=".9" />
      {[25, 33, 41, 49].map((y) => (
        <circle key={y} cx="34" cy={y} r=".9" fill={seam} />
      ))}
      <path d="M24 8 32 17 27 21 21 12Z" fill={fill} stroke={seam} strokeWidth=".8" strokeLinejoin="round" />
      <path d="M40 8 32 17 37 21 43 12Z" fill={fill} stroke={seam} strokeWidth=".8" strokeLinejoin="round" />
    </>
  ),
};

// art: { shape, fabric } from garmentFor; colour: the variant's colour name.
// Ids are per instance so tiles inside portals and the phone floor never share defs.
export default function GarmentArt({ art, colour }) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const path = GARMENT_PATHS[art?.shape];
  if (!path) return null;
  const hex = colourHex(colour) || FALLBACK_HEX;
  const fill = softFill(hex);
  const seam = seamColour(hex);
  const tex = art.fabric && FABRICS[art.fabric] ? `tx${uid}` : null;
  const fold = `fold${uid}`;
  return (
    <svg className="fp-art" viewBox="0 0 64 80" preserveAspectRatio="xMidYMid slice" aria-hidden="true" data-shape={art.shape}>
      <defs>
        {tex && FABRICS[art.fabric](tex)}
        <linearGradient id={fold} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#fff" stopOpacity=".18" />
          <stop offset=".55" stopColor="#fff" stopOpacity="0" />
          <stop offset="1" stopColor="#000" stopOpacity=".14" />
        </linearGradient>
      </defs>
      <rect width="64" height="80" fill={garmentGround(hex)} />
      <ellipse cx="32" cy="71" rx="17" ry="2.4" fill="#000" fillOpacity=".09" />
      <g transform="translate(0 6)">
        <path d={path} fill={fill} />
        {tex && <path d={path} fill={`url(#${tex})`} />}
        <path d={path} fill={`url(#${fold})`} />
        {DETAILS[art.shape]?.(seam, fill)}
        {/* A soft outline keeps the shape against its wash without a hard edge. */}
        <path d={path} fill="none" stroke={seam} strokeWidth=".8" strokeLinejoin="round" />
      </g>
    </svg>
  );
}
